import React, { useState, useMemo } from 'react';
import ARRL_SECTIONS from '../data/arrlSections';
import CustomSelect from './CustomSelect';

const VALID_SECTIONS = Object.keys(ARRL_SECTIONS);

const MODES = ['SSB', 'CW', 'FM', 'AM', 'FT8', 'FT4', 'RTTY', 'PSK31', 'OLIVIA', 'JS8', 'PACKET', 'SSTV', 'SAT'];

// WFD class format: digit(s) + one of H/I/O/M  e.g. "1O", "2I"
const WFD_CLASS_REGEX = /^\d+[HIOM]$/;
// ARFD class format: digit(s) + one of A/B/C/D/E/F  e.g. "2A", "1B"
const ARFD_CLASS_REGEX = /^\d+[ABCDEF]$/;

// All known amateur bands with frequency ranges (MHz)
const ALL_BANDS = [
  { label: '160m', min: 1.8, max: 2.0 },
  { label: '80m', min: 3.5, max: 4.0 },
  { label: '40m', min: 7.0, max: 7.3 },
  { label: '30m', min: 10.1, max: 10.15 },
  { label: '20m', min: 14.0, max: 14.35 },
  { label: '17m', min: 18.068, max: 18.168 },
  { label: '15m', min: 21.0, max: 21.45 },
  { label: '12m', min: 24.89, max: 24.99 },
  { label: '10m', min: 28.0, max: 29.7 },
  { label: '6m', min: 50.0, max: 54.0 },
  { label: '2m', min: 144.0, max: 148.0 },
  { label: '70cm', min: 420.0, max: 450.0 },
];

function getBandFromFreq(freq) {
  const f = parseFloat(freq);
  if (!f || f === 0) return 'SAT';
  for (const band of ALL_BANDS) {
    if (f >= band.min && f <= band.max) return band.label;
  }
  return 'Other';
}

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

function detectDiscrepancies(contact, eventType = 'WFD', allContacts = []) {
  const issues = [];
  
  if (!contact.contact_lat || !contact.contact_lon) {
    issues.push('missing_location');
  }
  
  if (!contact.contact_grid) {
    issues.push('missing_grid');
  }

  if (contact.class) {
    if (eventType === 'ARFD') {
      // ARFD: expect e.g. "2A". Flag if it doesn't match.
      if (!ARFD_CLASS_REGEX.test(contact.class)) {
        issues.push('invalid_class_format');
      }
    } else {
      // WFD: expect e.g. "1O". Flag common typo of 0 for O.
      if (/0/.test(contact.class) && !/O/.test(contact.class)) {
        issues.push('zero_instead_of_O');
      }
      if (!WFD_CLASS_REGEX.test(contact.class)) {
        issues.push('invalid_class_format');
      }
    }
  }
  
  if (contact.section && !VALID_SECTIONS.includes(contact.section) && contact.section !== 'DX') {
    issues.push('invalid_section');
  }
  
  if (contact.contact_callsign && /^\s|\s$/.test(contact.contact_callsign)) {
    issues.push('callsign_whitespace');
  }

  // Duplicate check: same callsign on the same band within 24 hours (excluding itself)
  if (contact.contact_callsign && contact.frequency) {
    const contactBand = getBandFromFreq(contact.frequency);
    const contactTime = new Date(contact.timestamp).getTime();
    const isDuplicate = allContacts.some(other => {
      if (other.id === contact.id) return false;
      if (other.contact_callsign !== contact.contact_callsign) return false;
      if (getBandFromFreq(other.frequency) !== contactBand) return false;
      return Math.abs(new Date(other.timestamp).getTime() - contactTime) < TWENTY_FOUR_HOURS_MS;
    });
    if (isDuplicate) {
      issues.push('duplicate_callsign_band');
    }
  }
  
  return issues;
}

function LogList({ contacts, onEdit, onDelete, token, apiUrl, user, eventType = 'WFD' }) {
  const [sortField, setSortField] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterText, setFilterText] = useState('');
  const [filterField, setFilterField] = useState('all');
  const [showOnlyIssues, setShowOnlyIssues] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const contactsWithIssues = useMemo(() => {
    return contacts.map(contact => ({
      ...contact,
      issues: detectDiscrepancies(contact, eventType, contacts)
    }));
  }, [contacts, eventType]);

  const filteredAndSorted = useMemo(() => {
    let result = [...contactsWithIssues];
    
    if (showOnlyIssues) {
      result = result.filter(c => c.issues.length > 0);
    }
    
    if (filterText) {
      const lower = filterText.toLowerCase();
      result = result.filter(c => {
        if (filterField === 'all') {
          return (
            c.contact_callsign?.toLowerCase().includes(lower) ||
            c.section?.toLowerCase().includes(lower) ||
            c.class?.toLowerCase().includes(lower) ||
            c.mode?.toLowerCase().includes(lower) ||
            c.operator_callsign?.toLowerCase().includes(lower) ||
            c.contact_grid?.toLowerCase().includes(lower)
          );
        }
        const value = c[filterField];
        return value?.toString().toLowerCase().includes(lower);
      });
    }
    
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'timestamp') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else if (sortField === 'frequency') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        aVal = aVal?.toString().toLowerCase() || '';
        bVal = bVal?.toString().toLowerCase() || '';
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
    
    return result;
  }, [contactsWithIssues, filterText, filterField, showOnlyIssues, sortField, sortDirection]);

  const issueCount = useMemo(() => {
    return contactsWithIssues.filter(c => c.issues.length > 0).length;
  }, [contactsWithIssues]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatTimestamp = (ts) => {
    const d = new Date(ts);
    return d.toISOString().replace('T', ' ').substring(0, 19) + 'Z';
  };

  const formatTimestampForInput = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toISOString().slice(0, 16);
  };

  const startEdit = (contact) => {
    setEditingId(contact.id);
    setEditForm({
      contactCallsign: contact.contact_callsign,
      frequency: contact.frequency,
      mode: contact.mode,
      class: contact.class,
      section: contact.section,
      gridsquare: contact.contact_grid || '',
      time: formatTimestampForInput(contact.timestamp),
      operatorCallsign: contact.operator_callsign
    });
  };

  const canEdit = (contact) => {
    if (user?.isAdmin) return true;
    return contact.operator_callsign === user?.callsign;
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (id) => {
    try {
      await onEdit(id, editForm);
      setEditingId(null);
      setEditForm({});
    } catch (err) {
      console.error('Error saving edit:', err);
    }
  };

  const SortHeader = ({ field, label }) => (
    <th 
      onClick={() => handleSort(field)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
    >
      {label}
      {sortField === field && (
        <span style={{ marginLeft: '4px' }}>
          {sortDirection === 'asc' ? '▲' : '▼'}
        </span>
      )}
    </th>
  );

  const IssueTag = ({ issue }) => {
    const labels = {
      missing_location: 'No Location',
      missing_grid: 'No Grid',
      zero_instead_of_O: '0 vs O',
      invalid_class_format: 'Bad Class',
      invalid_section: 'Bad Section',
      callsign_whitespace: 'Whitespace',
      duplicate_callsign_band: 'Dupe'
    };
    const titles = {
      missing_location: 'No location data',
      missing_grid: 'No grid square',
      zero_instead_of_O: 'Possible zero instead of letter O in class',
      invalid_class_format: 'Invalid class format',
      invalid_section: 'Invalid ARRL section',
      callsign_whitespace: 'Leading or trailing whitespace in callsign',
      duplicate_callsign_band: 'Same callsign worked on this band within 24 hours'
    };
    return (
      <span className="issue-tag" title={titles[issue] || issue}>
        {labels[issue] || issue}
      </span>
    );
  };

  return (
    <div className="log-list">
      <div className="log-list-controls">
        <div className="log-list-filters">
          <div style={{ width: '130px' }}>
            <CustomSelect
              value={filterField}
              onChange={setFilterField}
              options={[
                { value: 'all', label: 'All Fields' },
                { value: 'contact_callsign', label: 'Callsign' },
                { value: 'section', label: 'Section' },
                { value: 'class', label: 'Class' },
                { value: 'mode', label: 'Mode' },
                { value: 'operator_callsign', label: 'Operator' },
                { value: 'contact_grid', label: 'Grid' },
              ]}
            />
          </div>
          <input
            type="text"
            placeholder="Filter..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="log-filter-input"
            autoComplete="off"
            autoCorrect="off"
            style={{ textTransform: 'uppercase' }}
          />
          <button
            className={`btn btn-small ${showOnlyIssues ? '' : 'btn-secondary'}`}
            onClick={() => setShowOnlyIssues(!showOnlyIssues)}
          >
            Issues ({issueCount})
          </button>
        </div>
        <div className="log-list-stats">
          Showing {filteredAndSorted.length} of {contacts.length} contacts
        </div>
      </div>

      <div className="log-table-container">
        <table className="log-table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: '14%' }} />{/* Time */}
            <col style={{ width: '12%' }} />{/* Callsign */}
            <col style={{ width: '8%' }} />{/* Freq */}
            <col style={{ width: '7%' }} />{/* Mode */}
            <col style={{ width: '7%' }} />{/* Class */}
            <col style={{ width: '8%' }} />{/* Section */}
            <col style={{ width: '7%' }} />{/* Grid */}
            <col style={{ width: '10%' }} />{/* Operator */}
            <col style={{ width: '17%' }} />{/* Issues */}
            <col style={{ width: '70px' }} />{/* Actions — fixed, never collapses */}
          </colgroup>
          <thead>
            <tr>
              <SortHeader field="timestamp" label="Time (UTC)" />
              <SortHeader field="contact_callsign" label="Callsign" />
              <SortHeader field="frequency" label="Freq" />
              <SortHeader field="mode" label="Mode" />
              <SortHeader field="class" label="Class" />
              <SortHeader field="section" label="Section" />
              <SortHeader field="contact_grid" label="Grid" />
              <SortHeader field="operator_callsign" label="Operator" />
              <th>Issues</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSorted.map(contact => (
              <tr 
                key={contact.id} 
                className={contact.issues.length > 0 ? 'has-issues' : ''}
              >
                {editingId === contact.id ? (
                  <>
                    <td>
                      <input
                        type="datetime-local"
                        value={editForm.time}
                        onChange={(e) => setEditForm(prev => ({ ...prev, time: e.target.value }))}
                        className="log-edit-input"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={editForm.contactCallsign}
                        onChange={(e) => setEditForm(prev => ({ ...prev, contactCallsign: e.target.value.toUpperCase() }))}
                        className="log-edit-input"
                        autoComplete="off"
                        autoCorrect="off"
                        style={{ textTransform: 'uppercase' }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={editForm.frequency}
                        onChange={(e) => setEditForm(prev => ({ ...prev, frequency: e.target.value.replace(',', '.') }))}
                        className="log-edit-input log-edit-freq"
                      />
                    </td>
                    <td>
                      <div className="custom-select-sm">
                        <CustomSelect
                          value={editForm.mode}
                          onChange={(val) => setEditForm(prev => ({ ...prev, mode: val }))}
                          options={MODES}
                        />
                      </div>
                    </td>
                    <td>
                      <input
                        type="text"
                        value={editForm.class}
                        onChange={(e) => setEditForm(prev => ({ ...prev, class: e.target.value.toUpperCase() }))}
                        className="log-edit-input log-edit-small"
                        maxLength="4"
                        autoComplete="off"
                        autoCorrect="off"
                        style={{ textTransform: 'uppercase' }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={editForm.section}
                        onChange={(e) => setEditForm(prev => ({ ...prev, section: e.target.value.toUpperCase() }))}
                        className="log-edit-input log-edit-small"
                        maxLength="4"
                        autoComplete="off"
                        autoCorrect="off"
                        style={{ textTransform: 'uppercase' }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={editForm.gridsquare}
                        onChange={(e) => setEditForm(prev => ({ ...prev, gridsquare: e.target.value.toUpperCase() }))}
                        className="log-edit-input log-edit-small"
                        maxLength="6"
                        autoComplete="off"
                        autoCorrect="off"
                        style={{ textTransform: 'uppercase' }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={editForm.operatorCallsign}
                        onChange={(e) => setEditForm(prev => ({ ...prev, operatorCallsign: e.target.value.toUpperCase() }))}
                        className="log-edit-input log-edit-small"
                        maxLength="10"
                        autoComplete="off"
                        autoCorrect="off"
                        style={{ textTransform: 'uppercase' }}
                      />
                    </td>
                    <td></td>
                    <td className="log-actions">
                      <button className="btn-icon save" onClick={() => saveEdit(contact.id)} title="Save">✓</button>
                      <button className="btn-icon" onClick={cancelEdit} title="Cancel">✕</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="log-time">{formatTimestamp(contact.timestamp)}</td>
                    <td className="log-callsign">{contact.contact_callsign}</td>
                    <td>{parseFloat(contact.frequency).toFixed(3)}</td>
                    <td>{contact.mode}</td>
                    <td className={contact.issues.includes('zero_instead_of_O') || contact.issues.includes('invalid_class_format') ? 'cell-warning' : ''}>
                      {contact.class}
                    </td>
                    <td className={contact.issues.includes('invalid_section') ? 'cell-warning' : ''}>
                      {contact.section}
                    </td>
                    <td className={contact.issues.includes('missing_grid') ? 'cell-warning' : ''}>
                      {contact.contact_grid || '-'}
                    </td>
                    <td>{contact.operator_callsign}</td>
                    <td className="log-issues">
                      {contact.issues.map(issue => (
                        <IssueTag key={issue} issue={issue} />
                      ))}
                    </td>
                    <td className="log-actions" style={{ width: '64px' }}>
                      <button className="btn-icon" onClick={() => startEdit(contact)} title="Edit" style={{ visibility: canEdit(contact) ? 'visible' : 'hidden' }}>✎</button>
                      <button className="btn-icon delete" onClick={() => onDelete(contact.id)} title="Delete" style={{ visibility: canEdit(contact) ? 'visible' : 'hidden' }}>✕</button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default LogList;
